<template>
  <div
    v-if="modalTrigger"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div
      class="bg-white w-full max-w-4xl mx-4 p-6 rounded-xl shadow-lg relative overflow-hidden"
    >
      <div class="flex items-center pt-4 pb-10">
        <button
          @click="handleClose()"
          class="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl font-bold"
        >
          &times;
        </button>
        <h2 class="text-xl font-semibold text-gray-800">
          {{
            ifCurrentMode === "add"
              ? "Reimbursement Application Form"
              : "Edit Reimbursement Entry"
          }}
        </h2>
      </div>
      <!-- Step Indicators -->
      <div class="relative flex justify-between items-center mb-10">
        <template v-for="n in totalSteps" :key="n">
          <div class="relative flex flex-col items-center w-full">
            <div
              :class="[
                'z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300',
                currentStep >= n
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-500',
              ]"
            >
              {{ n }}
            </div>
            <span class="text-sm font-medium text-gray-700 mt-2 leading-snug">
              {{ stepTitles[n] }}
            </span>
            <div
              v-if="n < totalSteps"
              class="absolute top-3 left-1/2 w-full h-1"
            >
              <div
                class="w-full h-full bg-gray-300 rounded relative overflow-hidden"
              >
                <div
                  class="absolute top-0 left-0 h-full bg-green-600 transition-all duration-500 ease-in-out"
                  :style="{ width: currentStep > n ? '100%' : '0%' }"
                ></div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <component
        :is="currentComponent"
        :formData="formData"
        :ifCurrentMode="ifCurrentMode"
        :renderDataItems="renderDataItems"
        :errors="errors"
      />

      <!-- Navigation Buttons -->
      <div class="flex justify-between mt-8">
        <button
          @click="prevStep"
          :disabled="currentStep === 1"
          class="px-4 py-2 rounded bg-secondary text-white hover:bg-secondary disabled:opacity-50"
        >
          Previous
        </button>
        <div class="flex gap-2" v-if="ifCurrentMode === 'add'">
          <button
            v-if="currentStep < totalSteps"
            @click="nextStep"
            class="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary transition"
          >
            Next
          </button>

          <button
            v-else
            @click="createConfirmationHandler()"
            :disabled="isSubmitting"
            class="px-4 py-2 bg-primary text-white rounded hover:bg-orange-500 transition disabled:opacity-50"
          >
            <span v-if="isSubmitting">Submitting...</span>
            <span v-else>Submit</span>
          </button>
        </div>

        <div class="flex gap-2" v-else>
          <button
            v-if="currentStep < totalSteps"
            @click="nextStep"
            :disabled="renderDataItems.paymentstatus ? true : false"
            class="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary transition"
          >
            Next
          </button>

          <button
            v-else
            @click="updateConfirmationHandler()"
            :disabled="isSubmitting"
            class="px-4 py-2 bg-primary text-white rounded hover:bg-orange-500 transition disabled:opacity-50"
          >
            <span v-if="isSubmitting">Updating...</span>
            <span v-else>Update</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, reactive, computed, watch, inject } from "vue";
import StepOne from "@/components/partial/Application/SectionStepOne.vue";
import StepTwo from "@/components/partial/Application/SectionStepTwo.vue";
import StepThree from "@/components/partial/Application/SectionStepThree.vue";

export default {
  props: {
    modalTrigger: {
      type: Boolean,
      required: true,
    },
    ifCurrentMode: {
      type: String,
    },
    renderDataItems: {
      type: Object,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const Swal = inject("$swal");
    const axios = inject("$axios");
    const currentStep = ref(1);
    const totalSteps = 3;
    const isSubmitting = ref(false);
    const originalAttachment = ref();

    const formData = reactive({
      approvalno: null,
      company: null,
      membername: null,
      availmenttype: null,
      transtype: null,
      providername: null,
      admitdate: null,
      dischargedate: null,
      symptoms: null,
      contactperson: null,
      approvaltype: null,
      requestdate: null,
      declinedreason: null,
      declinedremarks: null,
      declineddate: null,
      declinedverify: 0,
      reasondothers: null,
      renderstartdate: null,
      graceperiod: 0,
      remarks: null,
      reimbursableamount: 0,
      AttachmentFiles: [
        {
          transactionnumber: null,
          particular: null,
          isdocument: 0,
        },
      ],
    });

    const errors = reactive({});

    const componentMap = {
      1: StepOne,
      2: StepTwo,
      3: StepThree,
    };

    const currentComponent = computed(() => componentMap[currentStep.value]);

    watch(
      () => props.modalTrigger,
      (newVal) => {
        if (newVal) {
          currentStep.value = 1;
          clearHandler();

          if (props.ifCurrentMode === "edit") {
            Object.assign(formData, props.renderDataItems);
            originalAttachment.value = JSON.parse(
              JSON.stringify(props.renderDataItems.AttachmentFiles)
            );
            formData.declinedverify =
              props.renderDataItems.declinedverify === 1 ? true : false;
          }
        }
      }
    );

    const nextStep = () => {
      // Clear previous field-level errors
      errors.company = "";
      errors.membername = "";
      errors.availmenttype = "";
      errors.transtype = "";
      errors.providername = "";
      errors.admitdate = "";
      errors.dischargedate = "";
      errors.symptoms = "";
      errors.contactperson = "";
      errors.approvaltype = "";
      errors.renderstartdate = "";
      errors.declinedreason = "";
      errors.requestdate = "";
      errors.declinedremarks = "";
      errors.declineddate = "";

      if (currentStep.value === 1) {
        if (!formData.company || formData.company.trim() === "") {
          errors.company = "Company is required";
        }
        if (!formData.membername || formData.membername.trim() === "") {
          errors.membername = "Membername is required";
        }
        if (!formData.availmenttype) {
          errors.availmenttype = "Availment type is required";
        }
        if (!formData.transtype && formData.availmenttype === "Out-Patient") {
          errors.transtype = "Transtype type is required";
        }
        if (!formData.providername) {
          errors.providername = "providername type is required";
        }
        if (!formData.admitdate) {
          errors.admitdate = "admitdate type is required";
        }
        if (
          !formData.dischargedate &&
          formData.availmenttype === "In-Patient"
        ) {
          errors.dischargedate = "dischargedate type is required";
        }
        if (!formData.symptoms) {
          errors.symptoms = "symptoms type is required";
        }

        if (!formData.contactperson) {
          errors.contactperson = "contactperson type is required";
        }

        if (!formData.approvaltype) {
          errors.approvaltype = "approvaltype type is required";
        }

        if (
          !formData.renderstartdate &&
          formData.approvaltype === "Pending Documents"
        ) {
          errors.renderstartdate = "renderstartdate type is required";
        }

        if (!formData.requestdate) {
          errors.requestdate = "declinedreason type is required";
        }

        if (!formData.declinedreason && formData.approvaltype === "Declined") {
          errors.declinedreason = "declinedreason type is required";
        }

        if (!formData.declinedremarks && formData.approvaltype === "Declined") {
          errors.declinedremarks = "declinedreason type is required";
        }

        if (!formData.declineddate && formData.approvaltype === "Declined") {
          errors.declineddate = "declineddate type is required";
        }

        if (Object.keys(errors).some((key) => errors[key])) return;
      }
      if (formData.approvaltype === "Declined") {
        currentStep.value = 3;
      } else {
        currentStep.value++;
      }
    };

    const prevStep = () => {
      if (formData.approvaltype === "Declined") {
        currentStep.value = 1;
      }
      if (currentStep.value > 1) currentStep.value--;
    };

    const handleClose = () => {
      emit("close", false);
    };

    const createConfirmationHandler = () => {
      Swal.fire({
        title: "Confirmation",
        text: "Are you sure you want to save this form?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          createProcessingHandler();
        }
      });
    };
    const createProcessingHandler = async () => {
      try {
        isSubmitting.value = true;
        formData.declinedverify = !formData.declinedverify
          ? 0
          : formData.declinedverify;
        console.log("Amount", formData.reimbursableamount);
        const { data } = await axios.post(
          "/portal/application/reimbursement",
          formData
        );
        if (data?.status) {
          await insertionAttachmentHandler(data.data.transactionnumber);
          await Swal.fire({
            icon: "success",
            title: "Successfully Saved",
            html: `The reimbursement request has been submitted`,
            confirmButtonText: "Ok",
          });

          emit("close");
          emit("success", true);
          currentStep.value = 1;
          clearHandler();
        } else {
          await Swal.fire({
            icon: "error",
            title: "Oops!",
            text:
              data?.message || "Something went wrong. Please try again later.",
          });
        }
      } catch (err) {
        console.error("Form submission error:", err);

        await Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text:
            err.response?.data?.message ||
            "Unable to process your request at the moment. Please try again.",
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    const updateConfirmationHandler = () => {
      Swal.fire({
        title: "Confirmation?",
        text: "Are you sure you want to update this form",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          updateProcessingHandler();
        }
      });
    };
    const updateProcessingHandler = async () => {
      try {
        isSubmitting.value = true;
        const { data } = await axios.put(
          `/portal/application/reimbursement/${formData.transactionnumber}`,
          formData
        );

        if (data?.status) {
          await insertionAttachmentHandler(formData.transactionnumber);
          await updateAttachmentHandler();
          Swal.fire({
            icon: "success",
            title: "Successfully Updated",
            html: `Reimbursement Application has been updated.`,
            confirmButtonText: "Ok",
          });

          emit("close");
          emit("success", true);
          currentStep.value = 1;
          clearHandler();
        } else {
          await Swal.fire({
            icon: "error",
            title: "Oops!",
            text:
              data?.message || "Something went wrong. Please try again later.",
          });
        }
      } catch (err) {
        console.log(err);
        await Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text:
            err.response?.data?.message ||
            "Unable to process your request at the moment. Please try again.",
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    const clearHandler = () => {
      Object.keys(formData).forEach((key) => {
        if (key === "graceperiod") {
          formData[key] = 30;
        } else if (Array.isArray(formData[key])) {
          formData[key] = [];
        } else {
          formData[key] = null;
        }
      });

      Object.keys(errors).forEach((key) => delete errors[key]);
    };

    const insertionAttachmentHandler = async (transactionnumber) => {
      try {
        const totalRows = formData.AttachmentFiles.length;

        console.log(formData.AttachmentFiles);

        for (let i = 0; i < totalRows; i++) {
          const isdocument = formData.AttachmentFiles[i].isdocument;
          const dataname = formData.AttachmentFiles[i].particular;

          const formDataItem = {
            transactionnumber: transactionnumber,
            particular: dataname,
            isdocument: isdocument,
          };

          console.log(formDataItem);
          await axios.post(
            "/portal/application/reimbursement/attachment",
            formDataItem
          );
        }
      } catch (err) {
        console.error(err);
      }
    };
    const updateAttachmentHandler = async () => {
      try {
        const totalRows = formData.AttachmentFiles.length;

        for (let i = 0; i < totalRows; i++) {
          const id = formData.AttachmentFiles[i].id;
          const isdocument = formData.AttachmentFiles[i].isdocument;

          await axios.put(
            `/portal/application/reimbursement/attachment/${id}`,
            {
              isdocument: isdocument,
              isdeleted: 0,
            }
          );
        }
      } catch (err) {
        console.error(err);
      }
    };

    const stepTitles = {
      1: "Assessment",
      2: "Attachment Document",
      3: "Confirmation",
    };

    return {
      currentStep,
      totalSteps,
      formData,
      errors,
      currentComponent,
      nextStep,
      prevStep,
      handleClose,
      createConfirmationHandler,
      createProcessingHandler,
      updateConfirmationHandler,
      updateProcessingHandler,
      stepTitles,
      isSubmitting,
      updateAttachmentHandler,
      originalAttachment,
    };
  },
};
</script>
